import type { Formation } from '~/composables/useFormations'
import { isValidPhone } from '~/utils/phoneValidation'
import { isValidEmail } from '~/utils/emailValidation'

export interface Attachment {
    type: string
    file: File | null
}

export const useCandidature = (formations: Ref<Formation[]>) => {
    const config = useRuntimeConfig()
    const apiBase = config.public.apiBase as string

    // File size limits
    const MAX_FILE_SIZE = 2 * 1024 * 1024 // 2MB per file
    const MAX_TOTAL_SIZE = 15 * 1024 * 1024 // 15MB total

    // State
    const form = ref({
        lastName: '',
        firstName: '',
        email: '',
        phone: '',
        formationSlug: '',
        portfolioUrl: '',
        cvUrl: '',
        message: ''
    })

    const motivationMode = ref('write') // 'write' | 'upload'
    const cvFile = ref<File | null>(null)
    const motivationFile = ref<File | null>(null)
    const attachments = ref<Attachment[]>([])
    const rgpdAccepted = ref(false)
    const subscribeNewsletter = ref(false)

    const isLoading = ref(false)
    const successMessage = ref('')
    const errorMessage = ref('')
    const fileSizeError = ref('') // New: for file size validation errors
    const isSubmitted = ref(false)

    // Scanning Logic
    type FileStatus = 'scanning' | 'ok' | 'error' | null
    const fileStatuses = ref<Record<string, FileStatus>>({})

    const isScanningOrError = computed(() => {
        return Object.values(fileStatuses.value).some(status => status === 'scanning' || status === 'error')
    })

    // Navigation Logic
    const currentStep = ref(1)
    const stepsCount = 4
    const stepTitles = ['Identité', 'Projet', 'Documents', 'Validation']

    // Computed
    const selectedFormation = computed(() => formations.value.find(f => f.slug === form.value.formationSlug))

    const isStep1Valid = computed(() => {
        return !!(form.value.lastName && form.value.firstName && isValidEmail(form.value.email) && isValidPhone(form.value.phone))
    })

    const isStep2Valid = computed(() => {
        return !!form.value.formationSlug
    })

    const isStep3Valid = computed(() => {
        return !!cvFile.value && (motivationMode.value === 'write' ? !!form.value.message : !!motivationFile.value)
    })

    const isStep4Valid = computed(() => {
        return rgpdAccepted.value
    })

    // Methods
    const addAttachment = () => {
        attachments.value.push({ type: '', file: null })
    }

    const removeAttachment = (index: number) => {
        attachments.value.splice(index, 1)
    }

    const scanFile = async (file: File, key: string) => {
        fileStatuses.value[key] = 'scanning'

        const formData = new FormData()
        formData.append('file', file)

        try {
            const data = await $fetch<{ status: string; message?: string }>(`${apiBase}/oxoweb/applications/scan`, {
                method: 'POST',
                body: formData
            })

            if (data.status === 'Clean') {
                fileStatuses.value[key] = 'ok'
            } else {
                fileStatuses.value[key] = null
                alert(data.message || `Fichier suspect retiré : ${file.name}`)
                if (key === 'cv') cvFile.value = null
                else if (key === 'motivation') motivationFile.value = null
                else if (key.startsWith('attachment_')) {
                    const parts = key.split('_')
                    const idx = parts.length > 1 ? parseInt(parts[1]!) : -1
                    if (attachments.value[idx]) attachments.value[idx].file = null
                }
            }
        } catch (e: any) {
            console.error(e)
            // Remove the file and reset status if scan fails to avoid blocking the whole form
            // with isScanningOrError
            fileStatuses.value[key] = null

            if (key === 'cv') cvFile.value = null
            else if (key === 'motivation') motivationFile.value = null
            else if (key.startsWith('attachment_')) {
                const parts = key.split('_')
                const idx = parts.length > 1 ? parseInt(parts[1]!) : -1
                if (attachments.value[idx]) attachments.value[idx].file = null
            }

            const errorData = e.response?._data as { message?: string } | undefined
            const msg = errorData?.message || e.message || "Erreur de connexion au service de scan"
            alert(`Le scan du fichier a échoué. Veuillez réessayer ou utiliser un autre fichier. (${msg})`)
        }
    }

    const handleFileChange = async (event: Event, type: 'cv' | 'motivation') => {
        const target = event.target as HTMLInputElement
        const file = target.files?.[0]
        fileSizeError.value = '' // Clear previous error

        if (file && file.size > MAX_FILE_SIZE) {
            fileSizeError.value = `Le fichier "${file.name}" dépasse la limite de 2 Mo. Taille actuelle : ${(file.size / (1024 * 1024)).toFixed(2)} Mo.`
            target.value = '' // Clear the input
            return
        }

        if (type === 'cv') {
            cvFile.value = file || null
            if (file) await scanFile(file, 'cv')
            else fileStatuses.value['cv'] = null
        } else if (type === 'motivation') {
            motivationFile.value = file || null
            if (file) await scanFile(file, 'motivation')
            else fileStatuses.value['motivation'] = null
        }
    }

    const handleAttachmentChange = async (event: Event, index: number) => {
        const target = event.target as HTMLInputElement
        const file = target.files?.[0]
        fileSizeError.value = '' // Clear previous error

        if (file && file.size > MAX_FILE_SIZE) {
            fileSizeError.value = `Le fichier "${file.name}" dépasse la limite de 2 Mo. Taille actuelle : ${(file.size / (1024 * 1024)).toFixed(2)} Mo.`
            target.value = '' // Clear the input
            return
        }

        if (attachments.value[index]) {
            attachments.value[index].file = file || null
            if (file) await scanFile(file, `attachment_${index}`)
            else fileStatuses.value[`attachment_${index}`] = null
        }
    }

    const nextStep = () => {
        if (currentStep.value < stepsCount) {
            currentStep.value++
            if (process.client) {
                const formElement = document.getElementById('candidature-form')
                if (formElement) formElement.scrollIntoView({ behavior: 'smooth' })
            }
        }
    }

    const prevStep = () => {
        if (currentStep.value > 1) {
            currentStep.value--
        }
    }

    const removeFile = (type: 'cv' | 'motivation' | string) => {
        if (type === 'cv') {
            cvFile.value = null
            fileStatuses.value['cv'] = null
        } else if (type === 'motivation') {
            motivationFile.value = null
            fileStatuses.value['motivation'] = null
        } else if (type.startsWith('attachment_')) {
            const index = parseInt(type.split('_')[1] || '0')
            if (attachments.value[index]) {
                attachments.value[index].file = null
                fileStatuses.value[type] = null
            }
        }
    }

    const submitApplication = async () => {
        isLoading.value = true
        successMessage.value = ''
        errorMessage.value = ''

        try {
            const formData = new FormData()
            formData.append('LastName', form.value.lastName)
            formData.append('FirstName', form.value.firstName)
            formData.append('Email', form.value.email)
            formData.append('Phone', form.value.phone)
            formData.append('FormationSlug', form.value.formationSlug)
            formData.append('PortfolioUrl', form.value.portfolioUrl || '')
            formData.append('Website', '') // Honeypot
            formData.append('SubscribeNewsletter', subscribeNewsletter.value.toString())

            if (motivationMode.value === 'write') {
                formData.append('Message', form.value.message)
            } else if (motivationFile.value) {
                formData.append('MotivationFile', motivationFile.value)
            }

            if (cvFile.value) formData.append('CvFile', cvFile.value)

            attachments.value.forEach((att) => {
                if (att.file) {
                    formData.append('OtherFiles', att.file)
                    formData.append('OtherFileTypes', att.type || 'Autre')
                }
            })

            const data = await $fetch(`${apiBase}/oxoweb/applications`, {
                method: 'POST',
                body: formData
            })

            if (data) {
                successMessage.value = "Candidature envoyée avec succès !"
                isSubmitted.value = true

                // Umami Tracking
                const win = (typeof window !== 'undefined' ? window : {}) as any
                if (win.umami) {
                    win.umami.track('Candidature - Sent', { formation: form.value.formationSlug })
                }

                // Reset
                form.value = { lastName: '', firstName: '', email: '', phone: '', formationSlug: '', portfolioUrl: '', cvUrl: '', message: '' }
                cvFile.value = null
                motivationFile.value = null
                attachments.value = []
                motivationMode.value = 'write'
                rgpdAccepted.value = false
                subscribeNewsletter.value = false
            }
        } catch (e: any) {
            console.error('Submission Error:', e)
            const errorData = e.response?._data

            if (typeof errorData === 'string') {
                errorMessage.value = errorData
            } else if (errorData && typeof errorData === 'object') {
                // Handle ModelState errors or JSON message
                errorMessage.value = errorData.message || errorData.title || "Une erreur est survenue."
                // Optional: Join specific validation errors if needed
            } else {
                errorMessage.value = "Erreur de connexion au serveur."
            }
        } finally {
            isLoading.value = false
        }
    }

    return {
        form,
        motivationMode,
        cvFile,
        motivationFile,
        attachments,
        rgpdAccepted,
        subscribeNewsletter,
        isLoading,
        successMessage,
        errorMessage,
        fileSizeError,
        isSubmitted,
        fileStatuses,
        isScanningOrError,
        currentStep,
        stepsCount,
        stepTitles,
        selectedFormation,
        isStep1Valid,
        isStep2Valid,
        isStep3Valid,
        isStep4Valid,
        addAttachment,
        removeAttachment,
        handleFileChange,
        handleAttachmentChange,
        nextStep,
        prevStep,
        removeFile,
        submitApplication,
        isValidEmail,
        isValidPhone,
        MAX_FILE_SIZE,
        MAX_TOTAL_SIZE
    }
}
